import './styles.scss';

import { Datetime, generateSearchableString } from '@/utils';
import { IssueHistoryRow, useIssueHistory } from '@/features/IssueHistory';

import { IssueHistoryFormDefinitions } from '@/features/IssueHistory/components/IssueHistoryForm/types';
import IssueHistoryTableColumns from '@/features/IssueHistory/components/IssueHistoryTable/IssueHistoryTableColumns';
import IssueHistoryTableRow from '@/features/IssueHistory/components/IssueHistoryTable/IssueHistoryTableRow';
import SimpleTable from '@/components/SimpleTable';
import { SortDirectionEnum } from '@/models/gen/graphql';

const IssueHistoryTable = (): JSX.Element => {
  const [{ issues: sortedIssues }] = useIssueHistory(({ issues, sortBy, sortDirection, search = '' }) => ({
    issues: Array.from(issues.keys())
      .filter((id: string) => {
        if ((search ?? '') === '') return true;
        const accident = issues.get(id);
        if (!accident) return false;
        const searchable = generateSearchableString(accident, IssueHistoryFormDefinitions.searchable);
        return searchable.includes(search?.toLowerCase?.());
      })
      .sort((a: string, b: string): number => {
        if (!sortDirection || !sortBy) return 0;
        const sortIssues = compareIssuesBy(sortBy);
        const result = sortIssues(issues.get(a), issues.get(b));
        if (sortDirection === SortDirectionEnum.Desc) return -result;
        return result;
      }),
  }));

  return <SimpleTable className="IssueHistoryTable" rows={sortedIssues} columns={IssueHistoryTableColumns} row={IssueHistoryTableRow} />;
};

const compareIssuesBy =
  (sortBy: string) =>
  (issueA: IssueHistoryRow, issueB: IssueHistoryRow): number => {
    if (!issueA || !issueB) return 0;
    switch (sortBy) {
      case 'STATION':
        return issueA.station.localeCompare(issueB.station);
      case 'DATETIME':
        return new Datetime(issueA.datetime).diff(issueB.datetime, 'millisecond');
      case 'CREATED_AT':
        return new Datetime(issueA.createdAt).diff(issueB.createdAt, 'millisecond');
      case 'TRIP_SERVICER_IATA_AIRLINE_CODE':
        return issueA.tripServicerIataAirlineCode.localeCompare(issueB.tripServicerIataAirlineCode);
      case 'TRIP_FLIGHT_NAME':
        let flightNameA = issueA.tripLoopName || issueA.tripFlightNumber.toString();
        let flightNameB = issueB.tripLoopName || issueB.tripFlightNumber.toString();
        return flightNameA.localeCompare(flightNameB);
      case 'DRIVER_NAME':
        return [issueA.driverFirstName, issueA.driverLastName]
          .join(' ')
          .localeCompare([issueB.driverFirstName, issueB.driverLastName].join(' '));
      case 'ROOT_CAUSE':
        return issueA.rootCause.localeCompare(issueB.rootCause);
      case 'TAXI_USED':
        return issueA.taxiUsed.localeCompare(issueB.taxiUsed);
      case 'TAXI_COST':
        return issueA.taxiCost - issueB.taxiCost;
      case 'RECOVERY_NEEDED':
        return issueA.recoveryNeeded === issueB.recoveryNeeded ? 0 : issueA.recoveryNeeded ? -1 : 1;
      case 'FLIGHT_LATE':
        return issueA.flightLate === issueB.flightLate ? 0 : issueA.flightLate ? -1 : 1;
      default:
        return 0;
    }
  };

export default IssueHistoryTable;
